import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form } from 'react-bootstrap';
import { useParams } from 'react-router-dom';

import Button from '../../../common/components/Button/Button';
import { useNavigation } from '../../../common/navigation';
import { usePokCore } from '../../../common/hooks/usePokCore';
import FormRow from '../../../app/components/FormRow';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import {
  GetProjectDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { DocumentControlSearch } from '../../components/DocumentControl/DocumentControlSearch';
import { useNotifications } from '../../../common/hooks/useNotifications';
import BudgetSelector from '../../components/Selects/BudgetSelector';
import { useTranslation } from '../../../common/hooks/useTranslation';

type ParamTypes = {
  id?: string;
};

export const DocumentControlView: React.FC = () => {
  const { id } = useParams<ParamTypes>();
  const { t, tk } = useTranslation('reportLng');
  const pok = usePokCore();
  const nav = useNavigation();
  const notifications = useNotifications();
  const [budgetId, setBudgetId] = useState<string>();
  const [project, setProject] = useState<GetProjectDto>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (id) {
      pok.projects
        .getById(id)
        .then(setProject)
        .catch(errorResponse => {
          notifications.caughtError(errorResponse);
        });
    }
  }, [id, notifications, pok.projects]);

  const handleClick = async (id: string) => {
    pok.estimateItems
      .getById(id)
      .then(item => nav.projectDetails(item.estimateByMonth?.project?.id, true))
      .catch(responseErrors => {
        notifications.caughtError(responseErrors as ResponseError);
      });
  };

  const getData = useMemo(
    () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        pok.estimateItems.getForDocumentControlByPortion(
          t,
          tk,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
          budgetId,
          id,
        )) as GridGetterFunction,
    [pok.estimateItems, budgetId, id, t, tk],
  );

  const getCount = useMemo(
    () =>
      ((filterText, filterColumns) =>
        pok.estimateItems.getForDocumentControlCount(
          filterText,
          filterColumns,
          budgetId,
          id,
        )) as GridCountFunction,
    [pok.estimateItems, budgetId, id],
  );

  const exportToXls = async () => {
    if (budgetId || id) {
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = id
        ? `${t(tk.controlling.control)}_${t(tk.prLang.genitiveProjectWithoutSpace)}_${project?.number}`
        : `${t(tk.controlling.control)}_${t(tk.controlling.genitiveDocuments)}`;
      try {
        setIsLoading(true);
        await pok.reports.getReportForDocumentControl(
          `${filename}_${currentDate}.xlsx`,
          budgetId,
          id,
        );
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        notifications.caughtError(error as ResponseError);
      }
    }
  };

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">
          {t(tk.controlling.documentList)}{' '}
          {id && project
            ? `${t(tk.controlling.for)} ${t(tk.prLang.genitiveProject)} ${project?.number} (${project?.name})`
            : ''}
        </Card.Title>
        {!id && (
          <Form className="d-grid gap-4 mb-3">
            <FormRow controlId="budget" label={t(tk.common.budget)}>
              <BudgetSelector
                documentControl={true}
                value={budgetId}
                onChange={option => setBudgetId(option?.value)}
                clearable
              />
            </FormRow>
          </Form>
        )}
        {(budgetId || id) && (
          <>
            <DocumentControlSearch
              getData={getData}
              getCount={getCount}
              onRowClick={handleClick}
              withFilterInColumns={true}
            />
            <Button
              variant="outline-secondary"
              onClick={exportToXls}
              isLoading={isLoading}
            >
              {t(tk.button.export)}
            </Button>
          </>
        )}
      </Card.Body>
    </Card>
  );
};
